// The Vacation Planner's arithmetic: how a stretch of time off splits between
// reduction time (requested as "Entered", unpaid, no review) and PATO
// (requested for review, paid), pay period by pay period.
//
// The planner shows it as a reconciliation: first the hours to work in each
// pay period the dates touch (TAWH, which needs no workday), then what the
// employee works on their other days at their workday (see the periods'
// toWork..adjustment). That is a second view of the figures below, not a
// second calculation: it lands on the same left-to-cover, so PATO and Entered
// time are unchanged.
//
// The idea it rests on: a commitment below full time leaves room in every pay
// period. An exempt employee at 80% owes TAWH (80% of the period's hours, see
// utils/tawh.js); the rest of the period's hours are theirs to take off
// without drawing on PATO. That room comes back every period, so a trip that
// straddles two periods gets two helpings of it.
//
// Per period touched by the trip:
//
//   scheduled       working days in the whole period x the standard day
//   allowance       scheduled - TAWH (exempt), or scheduled - target hours
//                   (non-exempt; no target set = no allowance) - the slack
//                   the standard schedule leaves over what is owed
//   close shortfall what each close date in the period but outside the trip
//                   costs: a closure pays a set number of hours (see
//                   closeDateCredit), so a standard day longer than that leaves
//                   a gap the reduction has to cover
//   already entered Entered time off on any day of the period, outside this
//                   trip - that reduction is spent
//   available       allowance - close shortfall - already entered (min 0)
//   usable          min(trip hours, available) - reduction only counts against
//                   time off in its own period
//   reduction used  the employee's chosen total (default: all usable), taken
//                   from the earliest period first
//   extra           hours the employee plans to work over their usual days in
//                   the period; each one frees an hour of trip from PATO
//   unpaid          hours the employee chooses to go unpaid rather than draw
//                   on PATO - Entered like reduction, but not paid; taken
//                   from the earliest period first
//   PATO            trip hours - reduction used - extra - unpaid
//
// Inside the trip, a working day counts for the standard day. A close date
// counts only for the part of the standard day the closure does not pay (zero
// when it pays the whole day). Weekends, and days already covered by a pending
// or approved request of the employee's own, are not planned at all.
//
// Pure: no React, no fetching. The page passes in what it has loaded.

import { STANDARD_WORK_DAY_HOURS, countWorkingDays, getHalfMonthPeriodsForMonth, toDateOnlyString, toStartOfDay } from './payPeriods';
import { tawhHoursForPeriod } from './tawh';
import { closeDateNameMap } from './timeOffDates';

const QUARTER = 0.25;
const EPSILON = 1e-9;

export const roundToQuarter = (value) => Math.round((Number(value) || 0) / QUARTER) * QUARTER;
const round2 = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;

const NO_APPROVAL_REQUIRED_REASON = 'no approval required';

// "Entered": approved without review, which is how reduction time is recorded.
// Not a status of its own - see TimeOffStatusBadge.
export const isEnteredRequest = (request) =>
  String(request?.status || '').toLowerCase() === 'approved'
  && String(request?.approval_reason || '').trim().toLowerCase() === NO_APPROVAL_REQUIRED_REASON;

// Pending, or approved after review: paid time off, drawn from PATO.
export const isPatoRequest = (request) => {
  const status = String(request?.status || '').toLowerCase();
  if (status === 'pending') return true;
  return status === 'approved' && !isEnteredRequest(request);
};

export const plannerAllocationForRequest = (request) => {
  const match = String(request?.notes || '').match(/\[\[VACATION-PLANNER-ALLOCATION\]\]([^\r\n]+)/);
  if (!match) return null;
  try {
    const value = JSON.parse(match[1]);
    return value && Array.isArray(value.days) ? value : null;
  } catch {
    return null;
  }
};

const isWeekday = (date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const eachDay = (start, end, fn) => {
  const cursor = new Date(start);
  while (cursor <= end) {
    fn(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
};

// The semi-monthly period a date falls in.
export const payPeriodFor = (date) => {
  const d = toStartOfDay(date);
  if (!d) return null;
  const [first, second] = getHalfMonthPeriodsForMonth(d.getFullYear(), d.getMonth());
  const period = d.getDate() <= 15 ? first : second;
  const startDate = toDateOnlyString(period.start);
  const endDate = toDateOnlyString(period.end);
  return {
    id: startDate,
    startDate,
    endDate,
    label: `${period.start.toLocaleDateString('en-US', { month: 'short' })} ${period.start.getDate()}-${period.end.getDate()}`,
  };
};

// The commitment in force for the pay period starting `periodStart`: the
// latest one effective on or before it, or the earliest when they all lie
// ahead. Mirrors commitmentOn in server/services/commitmentService.js.
//
// `history` rows are as the commitments endpoint returns them, any order.
// `fallback` (the employee record's mirror) is used only with no history.
export const commitmentForPeriod = (history, periodStart, fallback) => {
  const rows = (Array.isArray(history) ? history : [])
    .filter((row) => row?.effective_date)
    .slice()
    .sort((a, b) => String(a.effective_date).localeCompare(String(b.effective_date)));
  const pick = rows.length === 0
    ? null
    : rows.reduce((match, row) => (String(row.effective_date).slice(0, 10) <= periodStart ? row : match), rows[0]);
  const source = pick || fallback || {};
  const pct = Number(source.tawh_percent);
  const target = source.target_hours == null || source.target_hours === '' ? null : Number(source.target_hours);
  return {
    nonExempt: source.is_part_time === true,
    tawhPercent: Number.isFinite(pct) ? pct : 100,
    targetHours: Number.isFinite(target) ? target : null,
  };
};

const roundUpToHour = (hours) => {
  const value = Number(hours);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(value - EPSILON);
};

const positiveOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/*
  What a close date pays - the hours that may be logged against it. The same
  rule the View Hours projection and the timesheet validation run on
  (getScheduledDayHours / scheduledDayHours in pages/ViewHoursPage.jsx,
  expectedCloseDateHours in server/services/timesheetValidation.js):

    exempt      the standard 8-hour day at their commitment, rounded up to the
                hour - 80% gives 6.4 and pays 7
    non-exempt  their recent daily average (last four complete weeks / 5),
                falling back to the working day they told us about, then 8 -
                rounded up to the hour
*/
export const closeDateCredit = (commitment, { recentDailyAverage = null, averageWorkdayHours = null } = {}) => {
  if (!commitment?.nonExempt) {
    return roundUpToHour(STANDARD_WORK_DAY_HOURS * ((Number(commitment?.tawhPercent) || 0) / 100));
  }
  return roundUpToHour(
    positiveOrNull(recentDailyAverage) ?? positiveOrNull(averageWorkdayHours) ?? STANDARD_WORK_DAY_HOURS
  );
};

// One request's hours, day by day (date key -> hours), over its working days
// (weekdays that are not close dates). Follows resolveRequestDayHours in
// server/routes/connect.js: a single day takes its total, a multi-day request
// whose total is the auto-filled weekdays x 8 is a flat 8 a day, and a
// hand-edited total is spread evenly.
export const requestHoursByDay = (request, closeKeys) => {
  const start = toStartOfDay(String(request?.start_date || '').slice(0, 10));
  const end = toStartOfDay(String(request?.end_date || '').slice(0, 10));
  const byDay = new Map();
  if (!start || !end || end < start) return byDay;

  const allocation = plannerAllocationForRequest(request);
  if (allocation) {
    allocation.days.forEach((day) => {
      const key = String(day?.date || '').slice(0, 10);
      const date = toStartOfDay(key);
      const hours = Number(day?.hours);
      if (date && date >= start && date <= end && Number.isFinite(hours) && hours > 0) {
        byDay.set(key, roundToQuarter(hours));
      }
    });
    return byDay;
  }

  const workingKeys = [];
  let weekdayCount = 0;
  eachDay(start, end, (day) => {
    if (!isWeekday(day)) return;
    weekdayCount += 1;
    const key = toDateOnlyString(day);
    if (!closeKeys.has(key)) workingKeys.push(key);
  });
  if (workingKeys.length === 0) return byDay;

  const stored = request?.total_hours == null || request.total_hours === '' ? null : Number(request.total_hours);
  const storedHours = Number.isFinite(stored) && stored >= 0 ? stored : null;

  if (toDateOnlyString(start) === toDateOnlyString(end)) {
    let hours = storedHours;
    const startMinutes = Number(request?.start_minutes);
    const endMinutes = Number(request?.end_minutes);
    if (hours == null && Number.isInteger(startMinutes) && Number.isInteger(endMinutes) && endMinutes > startMinutes) {
      hours = (endMinutes - startMinutes) / 60;
    }
    byDay.set(workingKeys[0], roundToQuarter(Math.min(hours ?? STANDARD_WORK_DAY_HOURS, STANDARD_WORK_DAY_HOURS)));
    return byDay;
  }

  const autoTotal = weekdayCount * STANDARD_WORK_DAY_HOURS;
  if (storedHours == null || roundToQuarter(storedHours) === roundToQuarter(autoTotal)) {
    workingKeys.forEach((key) => byDay.set(key, STANDARD_WORK_DAY_HOURS));
    return byDay;
  }

  const perDay = roundToQuarter(storedHours / workingKeys.length);
  let assigned = 0;
  workingKeys.forEach((key, index) => {
    const hours = index === workingKeys.length - 1 ? roundToQuarter(storedHours - assigned) : perDay;
    assigned += perDay;
    byDay.set(key, Math.max(hours, 0));
  });
  return byDay;
};

/**
 * Plans a trip.
 *
 * @param {object} args
 * @param {Date|string} args.start / args.end   the trip, inclusive
 * @param {number} args.hoursPerDay              hours each planned day counts for
 * @param {Array}  args.commitments              commitment history rows
 * @param {object} args.fallbackCommitment       employee record (tawh_percent, is_part_time, target_hours)
 * @param {number} args.averageWorkdayHours      stated workday, the non-exempt closure fallback
 * @param {number} args.recentDailyAverage       non-exempt recent daily average from TimeCamp
 * @param {Array}  args.closeDates               close dates as the API returns them
 * @param {Array}  args.requests                 this employee's time-off requests
 * @param {number} args.reductionHours           reduction to use in all; null = as much as there is
 * @param {number} args.unpaidHours              hours to take unpaid instead of PATO, in all
 * @param {object} args.extraHoursByPeriod       period id -> extra hours planned
 * @param {Date}   args.today                    for "days left to bank extra hours"
 */
export const buildVacationPlan = ({
  start,
  end,
  hoursPerDay,
  commitments = [],
  fallbackCommitment = null,
  averageWorkdayHours = null,
  recentDailyAverage = null,
  closeDates = [],
  requests = [],
  reductionHours = null,
  unpaidHours = 0,
  extraHoursByPeriod = {},
  today = new Date(),
}) => {
  const from = toStartOfDay(start);
  const to = toStartOfDay(end);
  const dayHours = Math.max(0, Number(hoursPerDay) || 0);
  if (!from || !to || to < from) return null;

  const closeNames = closeDateNameMap(closeDates);
  const closeKeys = new Set(closeNames.keys());
  const todayKey = toDateOnlyString(today);
  const tripStartKey = toDateOnlyString(from);
  const tripEndKey = toDateOnlyString(to);
  const inTrip = (key) => key >= tripStartKey && key <= tripEndKey;

  // The employee's own live requests, as hours per day.
  const enteredByDay = new Map();
  const requestedDayKeys = new Set();
  for (const request of requests) {
    const entered = isEnteredRequest(request);
    const pato = isPatoRequest(request);
    if (!entered && !pato) continue;
    const startKey = String(request.start_date || '').slice(0, 10);
    const endKey = String(request.end_date || '').slice(0, 10);
    // Any day a live request covers is already asked for, whatever its hours.
    const rs = toStartOfDay(startKey);
    const re = toStartOfDay(endKey);
    if (rs && re && re >= rs) {
      eachDay(rs, re, (day) => {
        const key = toDateOnlyString(day);
        if (inTrip(key)) requestedDayKeys.add(key);
      });
    }
    if (entered) {
      requestHoursByDay(request, closeKeys).forEach((hours, key) => {
        enteredByDay.set(key, (enteredByDay.get(key) || 0) + hours);
      });
    }
    if (pato) {
      plannerAllocationForRequest(request)?.days.forEach((day) => {
        const key = String(day?.date || '').slice(0, 10);
        const hours = Number(day?.reductionHours);
        if (key && Number.isFinite(hours) && hours > 0) {
          enteredByDay.set(key, (enteredByDay.get(key) || 0) + round2(hours));
        }
      });
    }
  }

  const commitmentFor = (periodId) => commitmentForPeriod(commitments, periodId, fallbackCommitment);
  const creditFor = (periodId) => closeDateCredit(commitmentFor(periodId), { recentDailyAverage, averageWorkdayHours });

  // Every day of the trip, classified.
  const days = [];
  eachDay(from, to, (day) => {
    const key = toDateOnlyString(day);
    const period = payPeriodFor(day);
    let skip = null;
    if (!isWeekday(day)) skip = 'weekend';
    else if (requestedDayKeys.has(key)) skip = 'requested';
    const isClose = !skip && closeKeys.has(key);
    const closeCredit = isClose ? creditFor(period.id) : null;
    let hours = 0;
    if (isClose) hours = Math.max(0, round2(dayHours - closeCredit));
    else if (!skip) hours = dayHours;
    days.push({
      date: key,
      periodId: period.id,
      skip,
      isClose,
      closeName: isClose ? closeNames.get(key) : null,
      closeCredit,
      hours,
      enteredHours: 0,
      patoHours: 0,
    });
  });

  // Entered requests, kept whole so each period can list the ones it holds.
  const enteredRequests = requests
    .filter((request) => isEnteredRequest(request))
    .map((request) => ({
      id: request.id ?? null,
      start: String(request.start_date || '').slice(0, 10),
      end: String(request.end_date || '').slice(0, 10),
      byDay: requestHoursByDay(request, closeKeys),
    }));

  // The periods the trip touches, in order.
  const periodIds = [...new Set(days.map((day) => day.periodId))];
  const periods = periodIds.map((periodId) => {
    const meta = payPeriodFor(periodId);
    const periodStart = toStartOfDay(meta.startDate);
    const periodEnd = toStartOfDay(meta.endDate);
    const workingDays = countWorkingDays(meta.startDate, meta.endDate);
    // What the standard schedule puts in over the whole period.
    const scheduled = round2(workingDays * dayHours);
    const commitment = commitmentFor(meta.startDate);

    let target;
    if (!commitment.nonExempt) target = tawhHoursForPeriod(workingDays, commitment.tawhPercent);
    else target = commitment.targetHours == null ? scheduled : commitment.targetHours;
    const allowance = Math.max(0, round2(scheduled - target));

    const credit = creditFor(meta.startDate);
    // Close dates in the trip are planned as trip days; only the rest of the
    // period's closures come off the reduction here.
    let closeDays = 0;
    let alreadyEntered = 0;
    let bankDaysLeft = 0;
    eachDay(periodStart, periodEnd, (day) => {
      if (!isWeekday(day)) return;
      const key = toDateOnlyString(day);
      if (closeKeys.has(key)) {
        if (!inTrip(key)) closeDays += 1;
        return;
      }
      // Entered time on trip days is skipped above (the day is already
      // requested), so it is counted here once as spent reduction.
      alreadyEntered += enteredByDay.get(key) || 0;
      if (!inTrip(key) && key >= todayKey && !(enteredByDay.get(key) > 0)) bankDaysLeft += 1;
    });
    const closeShortfall = round2(closeDays * Math.max(0, dayHours - credit));
    alreadyEntered = round2(alreadyEntered);

    const enteredItems = enteredRequests
      .map((request) => {
        let hours = 0;
        request.byDay.forEach((value, key) => {
          if (key >= meta.startDate && key <= meta.endDate) hours += value;
        });
        return { id: request.id, start: request.start, end: request.end, hours: round2(hours) };
      })
      .filter((item) => item.hours > 0);

    const available = Math.max(0, round2(allowance - closeShortfall - alreadyEntered));
    const periodDays = days.filter((day) => day.periodId === periodId && !day.skip);
    const tripHours = round2(periodDays.reduce((total, day) => total + day.hours, 0));
    const usable = Math.min(tripHours, available);

    /*
      The same period as a reconciliation, the way TAWH is actually met: the
      hours to work in the whole pay period (its TAWH, or target hours), less
      what the employee will work and be paid for on the days they are not
      off. How those hours fall across the days does not matter - only the
      period's total does.

        hours to work   the period's TAWH or target hours
        you work        its other working days at the workday
        close dates     what each close date pays, never more than the day
        already entered reduction time already taken on those days, which
                        they will not be working after all
        adjustment      what the reduction's limits change: it only covers
                        days actually off, at most the workday each, and never
                        goes below nothing

      toWork - otherWorked - closePaid + enteredSpent + adjustment is always
      the left-to-cover figure (tripHours - usable) that everything after works
      from, so PATO and Entered time are as before. With no TAWH or target
      (a non-exempt employee with none set) there are no hours to work, and
      the days off simply count at the workday.
    */
    const hasTawh = !(commitment.nonExempt && commitment.targetHours == null);
    const leftAtFull = round2(tripHours - usable);
    const tripCloseCount = periodDays.filter((day) => day.isClose).length;
    // Working days of the period that are neither planned off nor close dates.
    const otherDays = workingDays - periodDays.length - closeDays;
    const toWork = hasTawh ? target : null;
    let otherWorked = 0;
    let closePaid = 0;
    let enteredSpent = 0;
    let leftRaw = tripHours;
    if (hasTawh) {
      otherWorked = round2(otherDays * dayHours);
      closePaid = round2((closeDays + tripCloseCount) * Math.min(dayHours, credit));
      enteredSpent = alreadyEntered;
      leftRaw = round2(toWork - otherWorked - closePaid + enteredSpent);
    }
    const adjustment = round2(leftAtFull - leftRaw);

    const rawExtra = Number(extraHoursByPeriod?.[periodId]);
    const extra = Number.isFinite(rawExtra) && rawExtra > 0 ? rawExtra : 0;

    return {
      ...meta,
      workingDays,
      // The whole period at the standard day, as View Hours shows it.
      periodHours: workingDays * STANDARD_WORK_DAY_HOURS,
      hasTawh,
      toWork,
      otherDays,
      tripCloseCount,
      otherWorked,
      closePaid,
      enteredSpent,
      adjustment,
      leftAtFull,
      scheduled,
      commitment,
      target,
      allowance,
      closeDays,
      closeDayCredit: credit,
      closeShortfall,
      alreadyEntered,
      enteredItems,
      available,
      // The most of this period's reduction the trip can use: reduction is
      // only worth anything against time off in its own period.
      usable,
      tripDays: periodDays.filter((day) => !day.isClose).length,
      tripHours,
      extra,
      bankDaysLeft,
      periodDays,
    };
  });

  // The reduction the employee chose to use, at most what the periods offer,
  // taken from the earliest period first.
  const maxReduction = round2(periods.reduce((total, period) => total + period.usable, 0));
  const reductionRequested = reductionHours == null || reductionHours === ''
    ? maxReduction
    : Math.max(0, Number(reductionHours) || 0);
  const reductionChosen = Math.min(reductionRequested, maxReduction);
  let reductionLeft = reductionChosen;
  const unpaidRequested = Math.max(0, Number(unpaidHours) || 0);
  let unpaidLeft = unpaidRequested;

  periods.forEach((period) => {
    const reductionUsed = round2(Math.min(period.usable, reductionLeft));
    reductionLeft = round2(reductionLeft - reductionUsed);
    const afterReduction = round2(period.tripHours - reductionUsed);
    // Extra hours only help in their own period, and only up to what is left.
    const extraUsed = round2(Math.min(period.extra, afterReduction));
    const beforeUnpaid = round2(afterReduction - extraUsed);
    // Unpaid only replaces PATO; there is nothing for it to do past that.
    const unpaidUsed = round2(Math.min(beforeUnpaid, unpaidLeft));
    unpaidLeft = round2(unpaidLeft - unpaidUsed);
    const patoHours = round2(beforeUnpaid - unpaidUsed);
    const enteredHours = round2(reductionUsed + extraUsed + unpaidUsed);

    // Entered first, from the first day of the trip in the period.
    let remaining = enteredHours;
    for (const day of period.periodDays) {
      const take = Math.min(day.hours, remaining);
      day.enteredHours = round2(take);
      day.patoHours = round2(day.hours - take);
      remaining = round2(remaining - take);
    }

    Object.assign(period, {
      reductionUsed,
      afterReduction,
      extraUsed,
      extraOver: round2(period.extra - extraUsed),
      beforeUnpaid,
      unpaidUsed,
      enteredHours,
      patoHours,
    });
    delete period.periodDays;
  });

  const sum = (key) => round2(periods.reduce((total, period) => total + period[key], 0));

  return {
    start: tripStartKey,
    end: tripEndKey,
    hoursPerDay: dayHours,
    days,
    periods,
    plannedDays: days.filter((day) => !day.skip && !day.isClose).length,
    weekdays: days.filter((day) => day.skip !== 'weekend').length,
    closeDays: days.filter((day) => day.isClose).length,
    workDayHours: round2(days.filter((day) => !day.skip && !day.isClose).reduce((total, day) => total + day.hours, 0)),
    closeDayHours: round2(days.filter((day) => day.isClose).reduce((total, day) => total + day.hours, 0)),
    requestedDays: days.filter((day) => day.skip === 'requested').length,
    // The pay periods' hours to work, and the reconciliation down to what is
    // left to cover with all of the reduction used (see the periods above).
    hoursToWork: round2(periods.reduce((total, period) => total + (period.toWork || 0), 0)),
    otherWorkedHours: sum('otherWorked'),
    closePaidHours: sum('closePaid'),
    enteredSpentHours: sum('enteredSpent'),
    adjustmentHours: sum('adjustment'),
    leftAtFullHours: sum('leftAtFull'),
    totalHours: sum('tripHours'),
    alreadyEnteredHours: sum('alreadyEntered'),
    maxReduction,
    reductionRequested: round2(reductionRequested),
    reductionChosen: round2(reductionChosen),
    reductionOver: round2(Math.max(0, reductionRequested - maxReduction)),
    // Reduction left on the table: hours that could have been Entered rather
    // than drawn from PATO.
    reductionUnused: round2(maxReduction - reductionChosen),
    afterReduction: sum('afterReduction'),
    enteredHours: sum('enteredHours'),
    patoHours: sum('patoHours'),
    extraHours: sum('extraUsed'),
    // PATO before any is taken unpaid - the most that can be.
    beforeUnpaid: sum('beforeUnpaid'),
    unpaidHours: sum('unpaidUsed'),
    unpaidOver: round2(unpaidLeft),
  };
};

// The time-off entries to make: at most one of each kind per pay period,
// since payroll only looks at the hours a period holds. `kind` is 'entered'
// (no review) or 'pato' (needs review).
//
// Every day away is on exactly one entry - knowing when somebody is out
// matters even when the day costs no PATO. Two entries cannot share a day (the
// server refuses an overlap), so a day that is part reduction and part PATO
// goes on the PATO entry, carrying only its PATO hours.
//
// A run carries on across a weekend, a close date the company pays in full and
// days of different hours - the request records its hours day by day (the
// planner allocation in its notes), and no day ever holds more than its own
// working hours, so the total always fits the days it spans. It breaks at a new
// pay period, an already-requested day or a day of the other kind.
export const groupDaysIntoRequests = (days, kind) => {
  const runs = [];
  let current = null;
  let currentPeriod = null;
  for (const day of days) {
    if (day.skip === 'weekend' || (day.isClose && day.hours === 0)) continue;
    let hours = 0;
    if (!day.skip) {
      if (kind === 'pato') hours = day.patoHours;
      else if (day.patoHours === 0) hours = day.enteredHours;
    }
    if (hours > 0) {
      const period = day.periodId ?? payPeriodFor(day.date)?.id ?? null;
      if (current && period !== currentPeriod) current = null;
      if (!current) {
        current = { start: day.date, end: day.date, hours: 0, days: 0, split: false };
        runs.push(current);
        currentPeriod = period;
      }
      current.end = day.date;
      current.hours = round2(current.hours + hours);
      current.days += 1;
      if (day.enteredHours > 0 && day.patoHours > 0) current.split = true;
    } else {
      current = null;
      currentPeriod = null;
    }
  }
  return runs;
};
